import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { num } from '@/lib/format';

/**
 * List chrome: filter chips, pagination, count readouts. Every list screen
 * uses these so filtering feels identical everywhere.
 */

/** A segmented row of mutually-exclusive filters, each with an optional count. */
export function ChipGroup<T extends string>({
  value,
  onChange,
  options,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{
    value: T;
    label: ReactNode;
    count?: number;
    tone?: 'negative' | 'warn';
  }>;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex h-7 items-center gap-1.5 rounded-sm border px-2 text-xs transition-colors duration-75',
              active
                ? 'border-signal-line bg-signal-muted text-ink font-medium'
                : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink'
            )}
          >
            {opt.label}
            {opt.count !== undefined ? (
              <span
                className={cn(
                  'text-2xs font-mono tabular-nums',
                  active ? 'text-signal-ink' : 'text-ink-faint',
                  opt.tone === 'negative' && 'text-negative',
                  opt.tone === 'warn' && 'text-warn'
                )}
              >
                {num(opt.count)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** A removable chip describing one applied filter. */
export function FilterChip({
  label,
  value,
  onRemove,
}: {
  label: string;
  value: ReactNode;
  onRemove: () => void;
}) {
  return (
    <span className="border-signal-line bg-signal-muted text-2xs flex h-6 items-center gap-1.5 rounded-sm border pr-1 pl-2">
      <span className="text-ink-faint">{label}</span>
      <span className="text-ink font-medium">{value}</span>
      <button
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="text-ink-faint hover:bg-surface-active hover:text-ink rounded-xs p-0.5"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

/**
 * Offset pagination. Shows a real record range, not just page numbers — "241–260
 * of 1,204" tells an operator where they are; "page 13" does not.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  className,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-2xs text-ink-faint font-mono tabular-nums">
        {num(first)}–{num(last)} of {num(total)}
      </span>
      <div className="ml-auto flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="size-3.5" />
        </Button>
        <span className="text-2xs text-ink-muted font-mono tabular-nums">
          {page} / {pages}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** Secondary nav for a section's sub-screens, flush with the header hairline. */
export function SubNav({ children }: { children: ReactNode }) {
  return <nav className="flex items-center gap-0.5">{children}</nav>;
}

export function SubNavItem({
  active,
  onClick,
  to,
  children,
  count,
}: {
  active: boolean;
  onClick?: () => void;
  /** Render as a link to this path instead of a button (a tab that is its own screen). */
  to?: string;
  children: ReactNode;
  count?: number;
}) {
  const className = cn(
    'flex h-7 items-center gap-1.5 border-b-2 px-2.5 text-xs transition-colors duration-75',
    active
      ? 'border-signal text-ink font-medium'
      : 'text-ink-muted hover:border-line-strong hover:text-ink border-transparent'
  );
  const content = (
    <>
      {children}
      {count ? (
        <span className="text-2xs text-ink-faint font-mono tabular-nums">{count}</span>
      ) : null}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        className={className}
        // `active` decides; the router's exact match never disagrees with it.
        activeOptions={{ exact: true, includeSearch: false }}
        activeProps={{}}
        aria-current={active ? 'page' : undefined}
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      onClick={onClick}
      className={className}
      aria-current={active ? 'true' : undefined}
    >
      {content}
    </button>
  );
}
